import { useNavigate, useParams } from "react-router-dom";
import CardHeader from "../../../components/Shared/CardHeader";
import { Button, DatePicker, Form, FormProps, Input, InputNumber, Select } from "antd";
import { PlusOutlined } from '@ant-design/icons';
import './styles.css';
import { isValidCPF, maskCPF } from "../../../utils/cpfUtils";
import { customizeRequiredMark } from "../../../utils/MarkRequiredForAntd";
import { allContractTypeOptions, allEducationLevelOptions, allQuarters } from "../../../Static/StaffsStatic";
import { formatCurrencyToImput, formatCurrencyWithoutSignToImput, parseCurrency } from "../../../utils/formatCurrency";
import { useContext, useState } from "react";
import { CreateStaffVO } from "../../../models/staffsModel";
import useLoading from "../../../hooks/useLoading";
import { CreateStaffAPI } from "../../../API/StaffsAPI";
import { toast } from "react-toastify";
import Loader from "../../../components/Shared/Loader";
import { CompanyContext } from "../../../contexts/companyContext";
import { getQuarterByMonth } from "../../../utils/quartersUtils";

function CreateStaff() {
    const [showAllRubrics, setShowAllRubrics] = useState<boolean>(false);
    const { company } = useContext(CompanyContext);
    const { loading, toggleLoading } = useLoading();
    const { serviceId } = useParams();
    const [form] = Form.useForm();
    const navigate = useNavigate();

    const dateFormat = "DD/MM/YYYY";

    function toggle() {
        setShowAllRubrics(!showAllRubrics);
    }

    const onFinish: FormProps<CreateStaffVO>['onFinish'] = async (values) => {
        if (new Date(values.admissionDate!.toString()).getFullYear() > company.year || (new Date(values.admissionDate!.toString()).getFullYear() == company.year && getQuarterByMonth(new Date(values.admissionDate!).getMonth()) > values.period!)) {
            toast.warning("Data de admissão incorreta de acordo com o periodo escolhido.");
            return;
        }

        try {
            toggleLoading(true);

            values.serviceId = serviceId!;

            if(company.counting !== 1)
                values.period = 0;

            await CreateStaffAPI(values).then(() => {
                toast.success("Staff criado com sucesso!");
                navigate(`/${serviceId}/staffs`);
            });
        } catch (error: any) {
            toast.error(error);
        } finally {
            toggleLoading(false);
        }
    };

    function calcTotalCostByRubrics() {
        const fields = [
            'salary', 'overtime', 'nighttime', 'unhealthiness', 'dangerousness',
            'paidRest', 'bonuses', 'vacation', 'additionalVacation',
            'thirteenthVacation', 'pecuniaryAllowance', 'fgtsVacation',
            'inssVacation', 'thirteenthSalary', 'fgtsThirteenthSalary',
            'inssThirteenthSalary', 'fgts', 'inss', 'inssThirdParty',
            'ratsat', 'educationSalary', 'familySalary',
            'transportationVoucher', 'mealVoucher',
            'daycareAssistance', 'others'
        ];

        const total = fields.reduce((acc, field) => {
            return acc + (form.getFieldValue(field) || 0);
        }, 0);

        var totalHours = form.getFieldValue("totalHours");

        form.setFieldsValue({ totalCost: total, costHour: total / totalHours });
    }

    function resetAllRubricValues() {
        form.setFieldsValue({
            additionalVacation: 0,
            bonuses: 0,
            dangerousness: 0,
            daycareAssistance: 0,
            educationSalary: 0,
            familySalary: 0,
            fgts: 0,
            fgtsThirteenthSalary: 0,
            fgtsVacation: 0,
            inss: 0,
            inssThirdParty: 0,
            inssThirteenthSalary: 0,
            inssVacation: 0,
            mealVoucher: 0,
            nighttime: 0,
            others: 0,
            overtime: 0,
            paidRest: 0,
            pecuniaryAllowance: 0,
            ratsat: 0,
            salary: 0,
            thirteenthSalary: 0,
            thirteenthVacation: 0,
            transportationVoucher: 0,
            unhealthiness: 0,
            vacation: 0,
            weeklyHoursNum: 0,
        });
    }

    function initValues() {
        var obj: CreateStaffVO = {
            serviceId: serviceId!,
            costHour: 0,
            totalCost: 0,
            totalHours: 0,
            additionalVacation: 0,
            bonuses: 0,
            dangerousness: 0,
            daycareAssistance: 0,
            educationSalary: 0,
            familySalary: 0,
            fgts: 0,
            fgtsThirteenthSalary: 0,
            fgtsVacation: 0,
            inss: 0,
            inssThirdParty: 0,
            inssThirteenthSalary: 0,
            inssVacation: 0,
            mealVoucher: 0,
            nighttime: 0,
            others: 0,
            overtime: 0,
            paidRest: 0,
            pecuniaryAllowance: 0,
            ratsat: 0,
            salary: 0,
            thirteenthSalary: 0,
            thirteenthVacation: 0,
            transportationVoucher: 0,
            unhealthiness: 0,
            vacation: 0,
        };

        return obj;
    }

    return (
        <>
            {loading && <Loader />}
            <div className='card'>
                <CardHeader
                    pageName={"Criação de Personagem"}
                    hasPageBefore={true}
                    linkPageBefore={`/${serviceId}/staffs`}
                    pageBeforeName="Cadastro de Funcionários"
                />
                <div className='card-body'>
                    <Form
                        form={form}
                        initialValues={initValues()}
                        layout="vertical"
                        onFinish={onFinish}
                        requiredMark={customizeRequiredMark}
                    >
                        <div className="staff-div-line">
                            {company.counting == 1 &&
                                <Form.Item
                                    label="Período:"
                                    name="period"
                                    className="w-100 mb-0"
                                    rules={[{ required: true, message: "O Período é obrigatório" }]}
                                >
                                    <Select options={allQuarters} placeholder="Selecione uma opção abaixo" size="large" className="w-100" />
                                </Form.Item>
                            }
                            <Form.Item
                                label="Nome Completo:"
                                name="name"
                                className={`w-100 ${company.counting !== 1 ? "ms-0" : "ms-4"} mb-0`}
                                rules={[{ required: true, message: "O Nome Completo é obrigatório" }]}
                            >
                                <Input placeholder="Digite o nome" size="large" />
                            </Form.Item>
                            <Form.Item
                                label="Razão Social:"
                                name="corporateName"
                                className="w-100 mx-4 mb-0"
                                rules={[{ required: false }]}
                            >
                                <Input placeholder="Razão Social" size="large" />
                            </Form.Item>
                            <Form.Item
                                label="CPF:"
                                name="cpf"
                                className="w-100 mb-0"
                                rules={[
                                    { required: true, message: 'CPF é obrigatório' },
                                    {
                                        validator: (_, value) =>
                                            !value || isValidCPF(value)
                                                ? Promise.resolve()
                                                : Promise.reject('CPF inválido'),
                                    },
                                ]}
                            >
                                <Input
                                    onChange={(e) => {
                                        const formatted = maskCPF(e.target.value);
                                        form.setFieldsValue({ cpf: formatted });
                                    }}
                                    className="w-100"
                                    placeholder="000.000.000-00"
                                    size="large"
                                />
                            </Form.Item>
                        </div>
                        <div className="staff-div-line">
                            <Form.Item
                                label="Matrícula/Id:"
                                name="registration"
                                className="w-100 mb-0"
                                rules={[{ required: false }]}
                            >
                                <Input placeholder="Matrícula/ID" size="large" />
                            </Form.Item>
                            <Form.Item
                                label="Nível de Escolaridade:"
                                name="educationLevel"
                                className="w-100 ms-4 mb-0"
                                rules={[{ required: true, message: "O Nível de Escolaridade é obrigatório" }]}
                            >
                                <Select options={allEducationLevelOptions} placeholder="Selecione uma opção abaixo" size="large" className="w-100" />
                            </Form.Item>
                            <Form.Item
                                label="Curso de Formação:"
                                name="trainingCourse"
                                className="w-100 mx-4 mb-0"
                                rules={[{ required: true, message: "O Curso de Formação é obrigatório" }]}
                            >
                                <Input placeholder="Digite o nome" size="large" />
                            </Form.Item>
                            <Form.Item
                                label="Tipo de Contrato:"
                                name="contractType"
                                className="w-100 mb-0"
                                rules={[{ required: true, message: "O Tipo de Contrato é obrigatório" }]}
                            >
                                <Select options={allContractTypeOptions} placeholder="Selecione uma opção abaixo" size="large" className="w-100" />
                            </Form.Item>
                        </div>
                        <div className="staff-div-line">
                            <Form.Item
                                label="Cargo:"
                                name="position"
                                className="w-100 mb-0"
                                rules={[{ required: true, message: "O Cargo é obrigatório" }]}
                            >
                                <Input placeholder="Digite o cargo" size="large" />
                            </Form.Item>
                            <Form.Item
                                label="Departamento:"
                                name="department"
                                className="w-100 ms-4 mb-0"
                                rules={[{ required: true, message: "O Departamento é obrigatório" }]}
                            >
                                <Input placeholder="Digite o departamento" size="large" />
                            </Form.Item>
                            <Form.Item
                                label="Classificação do RH:"
                                name="hrClassification"
                                className="w-100 mx-4 mb-0"
                                rules={[{ required: true, message: "O Classificação do RH é obrigatório" }]}
                            >
                                <Input placeholder="Digite a classificação do RH" size="large" />
                            </Form.Item>
                            <Form.Item
                                label="Conta Contábil:"
                                name="ledgerAccount"
                                className="w-100 mb-0"
                                rules={[{ required: true, message: "O Conta Contábil é obrigatório" }]}
                            >
                                <Input placeholder="Digite a conta contábil" size="large" />
                            </Form.Item>
                        </div>
                        <div className="staff-div-line">
                            <Form.Item
                                label="Data de Admissão:"
                                name="admissionDate"
                                className="w-100 mb-0"
                                rules={[{ required: true, message: "O Data de Admissão é obrigatório" }]}
                            >
                                <DatePicker format={dateFormat} className="w-100" size="large" />
                            </Form.Item>
                            <Form.Item
                                label="Data de Demissão:"
                                name="terminationDate"
                                className="w-100 ms-4 mb-0"
                                rules={[{ required: false }]}
                            >
                                <DatePicker format={dateFormat} className="w-100" size="large" />
                            </Form.Item>
                            <Form.Item
                                label="Nº Centro de Custo:"
                                name="costCenterNum"
                                className="w-100 mx-4 mb-0"
                                rules={[{ required: false }]}
                            >
                                <Input
                                    className="w-100"
                                    placeholder="Digite o numero de centro de custo"
                                    size="large"
                                />
                            </Form.Item>
                            <Form.Item
                                label="Nº de Horas Semanais:"
                                name="weeklyHoursNum"
                                className="w-100 mb-0"
                                rules={[{ required: false }]}
                            >
                                <InputNumber
                                    formatter={formatCurrencyWithoutSignToImput}
                                    parser={parseCurrency}
                                    precision={2}
                                    onChange={(e) => form.setFieldsValue({ weeklyHoursNum: e ? e : 0 })}
                                    step={0.01}
                                    className="w-100"
                                    controls={false}
                                    placeholder="Digite a quantidade de horas"
                                    size="large"
                                />
                            </Form.Item>
                        </div>
                        <div className="rubrics-div">
                            <div className="rubrics-div-line">
                                <Form.Item
                                    label="Horas Totais no Período:"
                                    name="totalHours"
                                    className="w-100 mb-0"
                                    rules={[{ required: true, message: "As Horas Totais é obrigatório!" }]}
                                >
                                    <InputNumber
                                        formatter={formatCurrencyWithoutSignToImput}
                                        parser={parseCurrency}
                                        precision={2}
                                        onChange={(e) => form.setFieldsValue({ totalHours: e ? e : 0, costHour: e ? form.getFieldValue("totalCost") / e : 0 })}
                                        step={0.01}
                                        className="w-100"
                                        controls={false}
                                        placeholder="00"
                                        size="large"
                                    />
                                </Form.Item>
                                <div className="d-flex w-100 align-items-end mx-4">
                                    <Form.Item
                                        label="Custo Total no Período:"
                                        name="totalCost"
                                        className="w-100 mb-0"
                                        rules={[{ required: true, message: "O Custo Total é obrigatório!" }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ totalCost: e ? e : 0, costHour: e ? e / form.getFieldValue("totalHours") : 0 }); resetAllRubricValues(); }}
                                            step={0.01}
                                            className="w-100 input-expand"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Button onClick={toggle} size="large" type="primary" className="btn-expand">Expandir</Button>
                                </div>
                                <Form.Item
                                    label="Custo Hora:"
                                    name="costHour"
                                    className="w-100 mb-0"
                                    rules={[{ required: true, message: "O Custo Hora é obrigatório!" }]}
                                >
                                    <InputNumber
                                        formatter={formatCurrencyToImput}
                                        parser={parseCurrency}
                                        precision={2}
                                        value={isNaN(form.getFieldValue("costHour")) ? 0 : form.getFieldValue("costHour")}
                                        step={0.01}
                                        disabled={true}
                                        className="w-100"
                                        controls={false}
                                        placeholder="00"
                                        size="large"
                                    />
                                </Form.Item>
                            </div>
                            <div className={!showAllRubrics ? "d-none" : ""}>
                                <div className="rubrics-div-line">
                                    <Form.Item
                                        label="Salário / Bolsa Estágio:"
                                        name="salary"
                                        className="w-100 mb-0"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ salary: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="Horas Extras:"
                                        name="overtime"
                                        className="w-100 mb-0 mx-4"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ overtime: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="Adicional Noturno:"
                                        name="nighttime"
                                        className="w-100 mb-0"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ nighttime: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="Insalubridade:"
                                        name="unhealthiness"
                                        className="w-100 mb-0 mx-4"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ unhealthiness: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="Periculosidade:"
                                        name="dangerousness"
                                        className="w-100 mb-0"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ dangerousness: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                </div>
                                <div className="rubrics-div-line">
                                    <Form.Item
                                        label="Descanso Remunerado:"
                                        name="paidRest"
                                        className="w-100 mb-0"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ paidRest: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="Gratificações / Prêmios:"
                                        name="bonuses"
                                        className="w-100 mb-0 mx-4"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ bonuses: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="Férias:"
                                        name="vacation"
                                        className="w-100 mb-0"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ vacation: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="Adicional de Férias:"
                                        name="additionalVacation"
                                        className="w-100 mb-0 mx-4"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ additionalVacation: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="1/3 Férias:"
                                        name="thirteenthVacation"
                                        className="w-100 mb-0"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ thirteenthVacation: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                </div>
                                <div className="rubrics-div-line">
                                    <Form.Item
                                        label="Abono Pecuniário:"
                                        name="pecuniaryAllowance"
                                        className="w-100 mb-0"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ pecuniaryAllowance: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="FGTS de Férias:"
                                        name="fgtsVacation"
                                        className="w-100 mb-0 mx-4"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ fgtsVacation: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="INSS Patronal de Férias:"
                                        name="inssVacation"
                                        className="w-100 mb-0"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ inssVacation: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="13º Salário:"
                                        name="thirteenthSalary"
                                        className="w-100 mb-0 mx-4"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ thirteenthSalary: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="FGTS do 13º Salário:"
                                        name="fgtsThirteenthSalary"
                                        className="w-100 mb-0"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ fgtsThirteenthSalary: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                </div>
                                <div className="rubrics-div-line">
                                    <Form.Item
                                        label="INSS Patronal 13º Salário:"
                                        name="inssThirteenthSalary"
                                        className="w-100 mb-0"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ inssThirteenthSalary: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="FGTS:"
                                        name="fgts"
                                        className="w-100 mb-0 mx-4"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ fgts: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="INSS Patronal:"
                                        name="inss"
                                        className="w-100 mb-0"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ inss: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="INSS Terceiros (Sistema S):"
                                        name="inssThirdParty"
                                        className="w-100 mb-0 mx-4"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ inssThirdParty: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="RAT/SAT:"
                                        name="ratsat"
                                        className="w-100 mb-0"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ ratsat: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                </div>
                                <div className="rubrics-div-line">
                                    <Form.Item
                                        label="Salário Educação:"
                                        name="educationSalary"
                                        className="w-100 mb-0"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ educationSalary: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="Salário Família:"
                                        name="familySalary"
                                        className="w-100 mb-0 mx-4"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ familySalary: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="Vale Transporte:"
                                        name="transportationVoucher"
                                        className="w-100 mb-0"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ transportationVoucher: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="Vale Refeição:"
                                        name="mealVoucher"
                                        className="w-100 mb-0 mx-4"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ mealVoucher: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                    <Form.Item
                                        label="Auxílio Creche:"
                                        name="daycareAssistance"
                                        className="w-100 mb-0"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ daycareAssistance: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="w-100"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                </div>
                                <div className="rubrics-div-line">
                                    <Form.Item
                                        label="Outros:"
                                        name="others"
                                        className="w-100 mb-0"
                                        rules={[{ required: false }]}
                                    >
                                        <InputNumber
                                            formatter={formatCurrencyToImput}
                                            parser={parseCurrency}
                                            precision={2}
                                            onChange={(e) => { form.setFieldsValue({ others: e ? e : 0 }); calcTotalCostByRubrics(); }}
                                            step={0.01}
                                            className="input-others"
                                            controls={false}
                                            placeholder="00"
                                            size="large"
                                        />
                                    </Form.Item>
                                </div>
                            </div>
                        </div>
                        <div className="d-flex w-100 align-items-center justify-content-end">
                            <Button htmlType="submit" size="large" className="btn-insert-excel" type="primary" icon={<PlusOutlined />}>Cadastrar</Button>
                        </div>
                    </Form>
                </div>
            </div>
        </>
    )
}

export default CreateStaff;